"use client"

import { useState, useRef } from "react"
import { Search, Zap, Check } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface SearchModeToggleProps {
    onDeepResearchClick: () => void
    onFastSearchClick: () => void
    fastRemaining: number
    proRemaining: number
}

export function SearchModeToggle({
    onDeepResearchClick,
    onFastSearchClick,
    fastRemaining,
    proRemaining,
}: SearchModeToggleProps) {
    const [researchMode, setResearchMode] = useState<"deep-research" | "fast-search">("fast-search")
    const [popoverOpen, setPopoverOpen] = useState(false)
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const clearHoverTimeout = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
            hoverTimeoutRef.current = null
        }
    }

    return (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
                <div
                    className="flex items-center bg-slate-100 border border-slate-200 rounded-tl-lg rounded-tr-lg p-0.5 gap-0.5 cursor-pointer"
                    onMouseEnter={() => {
                        hoverTimeoutRef.current = setTimeout(() => {
                            setPopoverOpen(true)
                        }, 600)
                    }}
                    onMouseLeave={() => {
                        clearHoverTimeout()
                        setPopoverOpen(false)
                    }}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            clearHoverTimeout()
                            setPopoverOpen(false)
                            setResearchMode("fast-search")
                            onFastSearchClick()
                        }}
                        className={cn(
                            "flex items-center gap-1.5 px-3 py-1.5 rounded-tl-lg rounded-tr-lg text-[12px] font-semibold transition-all duration-200 cursor-pointer",
                            researchMode === "fast-search"
                                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm"
                                : "text-slate-500 hover:text-slate-700"
                        )}
                    >
                        <Zap className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Fast</span>
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            clearHoverTimeout()
                            setPopoverOpen(false)
                            setResearchMode("deep-research")
                            onDeepResearchClick()
                        }}
                        className={cn(
                            "flex items-center gap-1.5 px-4 py-1.5 rounded-tl-lg rounded-tr-lg text-[12px] font-semibold transition-all duration-200 cursor-pointer",
                            researchMode === "deep-research"
                                ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-white shadow-sm"
                                : "text-slate-500 hover:text-slate-700"
                        )}
                    >
                        <Search className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Pro</span>
                    </button>
                </div>
            </PopoverTrigger>
            <PopoverContent
                className="w-[480px] p-3 bg-white/95 backdrop-blur-md border-slate-200 shadow-2xl rounded-2xl"
                sideOffset={12}
                align="start"
                alignOffset={-10}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onMouseEnter={clearHoverTimeout}
                onMouseLeave={() => setPopoverOpen(false)}
            >
                <div className="space-y-2">
                    <div className="px-1 pt-1 pb-0.5">
                        <h4 className="text-[13px] font-bold text-slate-900 leading-tight">Search Mode</h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">Choose how AI researches your topic</p>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setResearchMode("fast-search")
                                onFastSearchClick()
                                setPopoverOpen(false)
                            }}
                            className={cn(
                                "group relative flex flex-col items-start rounded-xl p-1 border text-left cursor-pointer",
                                researchMode === "fast-search"
                                    ? "bg-blue-50/80 border border-blue-500 shadow-md"
                                    : "bg-slate-50/80 border-transparent hover:bg-slate-100"
                            )}
                        >
                            <div className="flex flex-col gap-2 w-full">
                                <div className="w-full aspect-[16/9] rounded-lg overflow-hidden flex-shrink-0 border border-slate-100 bg-slate-100 bg-cover bg-center" style={{ backgroundImage: "url('https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1774259227528_4c5h30trpde_ChatGPT Image Mar 23, 2026, 02_49_08 PM.png')" }}>
                                </div>
                                <div className="flex-1 min-w-0 px-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className={cn("text-[13px] font-bold", researchMode === "fast-search" ? "text-slate-900" : "text-slate-600")}>Fast Mode</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 leading-relaxed font-medium">
                                        Quick draft
                                    </p>
                                </div>
                            </div>
                            <div className="absolute bottom-2 right-2 text-[10px] font-bold text-blue-600/60 bg-blue-50/50 px-1.5 py-0.5 rounded-md border border-blue-200">
                                {fastRemaining} {fastRemaining === 1 ? 'deck' : 'decks'} remaining
                            </div>
                            {researchMode === "fast-search" && (
                                <div className="absolute top-3 right-3 bg-blue-500 text-white rounded-full p-0.5 shadow-sm">
                                    <Check className="h-3 w-3 stroke-[3]" />
                                </div>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setResearchMode("deep-research")
                                onDeepResearchClick()
                                setPopoverOpen(false)
                            }}
                            className={cn(
                                "group relative flex flex-col items-start rounded-xl p-1 border text-left cursor-pointer",
                                researchMode === "deep-research"
                                    ? "bg-yellow-100/60 border border-yellow-500 shadow-md"
                                    : "bg-yellow-50/80 border-transparent hover:bg-yellow-100/60"
                            )}
                        >
                            <div className="flex flex-col gap-2 w-full">
                                <div className="w-full aspect-[16/9] rounded-lg overflow-hidden flex-shrink-0 border border-slate-100 bg-slate-100 bg-contain bg-center" style={{ backgroundImage: "url('https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1774260312983_aygvzoy862d_ChatGPT Image Mar 23, 2026, 03_35_05 PM.png')" }}>
                                </div>
                                <div className="flex-1 min-w-0 px-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className={cn("text-[13px] font-bold", researchMode === "deep-research" ? "text-yellow-700" : "text-yellow-700")}>Pro Mode</span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 leading-relaxed font-medium">
                                        Detailed research
                                    </p>
                                </div>
                            </div>
                            <div className="absolute bottom-2 right-2 text-[10px] font-bold text-blue-600/60 bg-blue-50/50 px-1.5 py-0.5 rounded-md border border-blue-200">
                                {proRemaining} {proRemaining === 1 ? 'deck' : 'decks'} remaining
                            </div>
                            {researchMode === "deep-research" && (
                                <div className="absolute top-3 right-3 bg-yellow-500 text-white rounded-full p-0.5 shadow-sm">
                                    <Check className="h-3 w-3 stroke-[3]" />
                                </div>
                            )}
                        </button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
